import React, { useState, useCallback } from "react";
import { Typography, Card, Box, Grid, Tooltip as MuiTooltip, Modal, Avatar, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BackdropScreen from "bundles/common/components/BackdropScreen";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { FileText, UserPlus, Clock, Users } from "lucide-react";
import sh from "bundles/common/utils/sh";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell
} from "recharts";

const useStyles = makeStyles((theme) => ({
  kpiItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5)
  },
  kpiIconBox: {
    width: 48,
    height: 48,
    borderRadius: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  }
}));

const DashboardMetrics = ({ metricsData, loading = false }) => {
  const intl = useIntl();
  const theme = useTheme();
  const classes = useStyles();
  const [funnelModalOpen, setFunnelModalOpen] = useState(false);
  const [funnelModalCandidates, setFunnelModalCandidates] = useState([]);
  const [funnelModalColumnName, setFunnelModalColumnName] = useState("");
  const [funnelModalLoading, setFunnelModalLoading] = useState(false);
  const kpiPalette = {
    applications: {
      icon: "hsl(199, 89%, 48%)",
      bg: "hsl(199, 89%, 93%)"
    },
    conversion_rate: {
      icon: "hsl(152, 60%, 45%)",
      bg: "hsl(152, 60%, 92%)"
    },
    avg_time_to_hire: {
      icon: "hsl(262, 60%, 58%)",
      bg: "hsl(262, 60%, 93%)"
    },
    active_candidates: {
      icon: "hsl(35, 92%, 50%)",
      bg: "hsl(35, 92%, 92%)"
    }
  };
  const funnelBarPalette = [
    "#0EA5E9",
    "#22C55E",
    "#8B5CF6",
    "#14B8A6",
    "#6366F1",
    "#84CC16"
  ];

  const kpis = metricsData?.kpis || {};
  const meta = metricsData?.meta || {};
  const selectedPeriod = meta?.period || "monthly";
  const applicationsOverTime = metricsData?.applications_over_time || {};
  const boardFunnel = metricsData?.board_funnel || {};
  const timeToHire = metricsData?.time_to_hire || {};
  const applicationOrigin = metricsData?.application_origin || {};

  const monthBucketFormatter = new Intl.DateTimeFormat(intl.locale || "en", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC"
  });
  const monthOnlyFormatter = new Intl.DateTimeFormat(intl.locale || "en", {
    month: "short",
    timeZone: "UTC"
  });
  const dayBucketFormatter = new Intl.DateTimeFormat(intl.locale || "en", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC"
  });

  const toChartDateLabel = (value, bucketUnit = "month", options = {}) => {
    const { monthOnly = false } = options;

    if (!value) {
      return "-";
    }
    const date = new Date(value);

    if (bucketUnit === "day") {
      return dayBucketFormatter.format(date);
    }

    if (bucketUnit === "quarter") {
      const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
      return `Q${quarter} ${String(date.getUTCFullYear()).slice(-2)}`;
    }

    if (bucketUnit === "year") {
      return String(date.getUTCFullYear());
    }

    if (bucketUnit === "month" && monthOnly) {
      return monthOnlyFormatter.format(date);
    }

    return monthBucketFormatter.format(date);
  };

  const applicationsBucketUnit = selectedPeriod === "monthly" ? "day" : "month";
  const applicationsUseMonthOnlyLabels = ["quarterly", "yearly", "all"].includes(selectedPeriod);
  const applicationsSeries = (applicationsOverTime.series || []).map((item) => ({
    bucket_start: item.bucket_start,
    bucket_label: toChartDateLabel(item.bucket_start, applicationsBucketUnit, {
      monthOnly: applicationsUseMonthOnlyLabels
    }),
    value: Number(item.value || 0)
  }));

  const funnelSeries = (boardFunnel.columns || []).map((item) => ({
    id: item.id,
    name: item.name,
    entered_count: Number(item.entered_count || 0)
  }));
  const funnelMaxValue = Math.max(...funnelSeries.map((item) => Number(item.entered_count || 0)), 1);

  const handleFunnelBarClick = useCallback(async (columnId, columnName) => {
    setFunnelModalColumnName(columnName);
    setFunnelModalCandidates([]);
    setFunnelModalOpen(true);
    setFunnelModalLoading(true);
    try {
      const res = await sh.get("/recruiter/board_funnel_candidates", {
        params: { column_id: columnId, period: selectedPeriod },
      });
      setFunnelModalCandidates(res?.data?.data || []);
    } catch (e) {
      console.error(e);
      setFunnelModalCandidates([]);
    } finally {
      setFunnelModalLoading(false);
    }
  }, [selectedPeriod]);

  const timeToHireBucketUnit = meta?.bucket || "month";
  const timeToHireUseMonthOnlyLabels = ["monthly", "all"].includes(selectedPeriod) && timeToHireBucketUnit === "month";
  const timeToHireSeries = (timeToHire.series || []).map((item) => ({
    bucket_start: item.bucket_start,
    bucket_label: toChartDateLabel(item.bucket_start, timeToHireBucketUnit, {
      monthOnly: timeToHireUseMonthOnlyLabels
    }),
    median_days: item.median_days == null ? null : Number(item.median_days),
    hires_count: Number(item.hires_count || 0)
  }));

  const applicationOriginSeries = (applicationOrigin.series || []).map((item) => ({
    key: item.key,
    name: item.label,
    value: Number(item.value || 0),
    percent: Number(item.percent || 0)
  }));

  const applicationOriginColor = (entry) => {
    const key = String(entry?.key || "").toLowerCase();
    if (key === "platform") return "#0EA5E9";
    if (key === "mailing") return "#F59E0B";
    if (key === "social") return "#EC4899";
    if (key === "talent_team") return "#8B5CF6";
    if (key === "cvtheque") return "#22C55E";
    return "#475569";
  };

  const applicationsValue = Number(kpis?.applications?.value || 0);
  const applicationsDelta = kpis?.applications?.delta?.percent_change;

  const avgTimeValue = kpis?.avg_time_to_hire?.value_days;
  const avgTimeDelta = kpis?.avg_time_to_hire?.delta?.percent_change;

  const activeCandidatesValue = Number(kpis?.active_candidates?.value || 0);

  const deltaText = (value) => {
    if (value == null) {
      return "N/A";
    }
    const rounded = Number(value).toFixed(1);
    return `${Number(value) >= 0 ? "+" : ""}${rounded}%`;
  };

  const deltaColor = (value) => {
    if (value == null) {
      return "text.secondary";
    }
    return Number(value) >= 0 ? "success.main" : "error.main";
  };

  const chartCardSx = {
    padding: "16px",
    borderRadius: 2,
    height: "100%",
    minHeight: 320,
    display: "flex",
    flexDirection: "column",
    boxShadow: "none",
    border: `1px solid ${theme.palette.background.neutral}`
  };

  const kpiCardSx = {
    padding: "16px",
    borderRadius: 2,
    gap: "20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "none",
    border: `1px solid ${theme.palette.background.neutral}`
  };

  const emptyChartState = (message) => (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  return(
    <Box
      sx={{
        bgcolor: "#FFF",
        p: 3,
        borderRadius: 2,
      }}
    >
      <BackdropScreen open={loading} />
      {!loading && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card sx={kpiCardSx}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                  <FormattedMessage id="recruiter.metrics.performance_snapshot" defaultMessage="Performance Snapshot" />
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.kpiItem}>
                      <Box className={classes.kpiIconBox} sx={{ bgcolor: kpiPalette.applications?.bg || "hsl(199, 89%, 93%)" }}>
                        <FileText size={24} color={kpiPalette.applications?.icon || "hsl(199, 89%, 48%)"} />
                      </Box>
                      <Box>
                        <MuiTooltip
                          title={intl.formatMessage(
                            {
                              id: "recruiter.metrics.kpi.applications.tooltip",
                              defaultMessage: "{title}: Total number of applications received by your company."
                            },
                            {
                              title: intl.formatMessage({ id: "menu.applications", defaultMessage: "Applications" })
                            }
                          )}
                          arrow placement="top"
                        >
                          <Typography variant="body2" sx={{ cursor: "help", width: "fit-content" }}>
                            <FormattedMessage id="menu.applications" defaultMessage="Applications" />
                          </Typography>
                        </MuiTooltip>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "Segoe UI Bold, sans-serif" }}>
                            {applicationsValue}
                          </Typography>
                          <Typography variant="caption" sx={{ color: deltaColor(applicationsDelta), fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                            ({deltaText(applicationsDelta)})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.kpiItem}>
                      <Box className={classes.kpiIconBox} sx={{ bgcolor: kpiPalette.conversion_rate?.bg || "hsl(152, 60%, 92%)" }}>
                        <UserPlus size={24} color={kpiPalette.conversion_rate?.icon || "hsl(152, 60%, 45%)"} />
                      </Box>
                      <Box>
                        <MuiTooltip
                          title={intl.formatMessage(
                            {
                              id: "recruiter.metrics.kpi.conversion_rate.tooltip",
                              defaultMessage: "{title}: Conversion from application starts to submitted applications."
                            },
                            {
                              title: intl.formatMessage({ id: "recruiter.metrics.conversion_rate", defaultMessage: "Conversion Rate" })
                            }
                          )}
                          arrow placement="top"
                        >
                          <Typography variant="body2" sx={{ cursor: "help", width: "fit-content" }}>
                            <FormattedMessage id="recruiter.metrics.conversion_rate" defaultMessage="Conversion Rate" />
                          </Typography>
                        </MuiTooltip>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "Segoe UI Bold, sans-serif" }}>
                          N/A
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.kpiItem}>
                      <Box className={classes.kpiIconBox} sx={{ bgcolor: kpiPalette.avg_time_to_hire?.bg || "hsl(262, 60%, 93%)" }}>
                        <Clock size={24} color={kpiPalette.avg_time_to_hire?.icon || "hsl(262, 60%, 58%)"} />
                      </Box>
                      <Box>
                        <MuiTooltip
                          title={intl.formatMessage(
                            {
                              id: "recruiter.metrics.kpi.avg_time_to_hire.tooltip",
                              defaultMessage: "{title}: Average number of days from first Pending stage to Hired for hires."
                            },
                            {
                              title: intl.formatMessage({ id: "recruiter.metrics.avg_time_to_hire", defaultMessage: "Avg Time to Hire" })
                            }
                          )}
                          arrow
                          placement="top"
                        >
                          <Typography variant="body2" sx={{ cursor: "help", width: "fit-content" }}>
                            <FormattedMessage id="recruiter.metrics.avg_time_to_hire" defaultMessage="Avg Time to Hire" />
                          </Typography>
                        </MuiTooltip>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "Segoe UI Bold, sans-serif" }}>
                            {avgTimeValue == null ? "N/A" : `${avgTimeValue}d`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: deltaColor(avgTimeDelta), fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                            ({deltaText(avgTimeDelta)})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.kpiItem}>
                      <Box className={classes.kpiIconBox} sx={{ bgcolor: kpiPalette.active_candidates?.bg || "hsl(35, 92%, 92%)" }}>
                        <Users size={24} color={kpiPalette.active_candidates?.icon || "hsl(35, 92%, 50%)"} />
                      </Box>
                      <Box>
                        <MuiTooltip
                          title={intl.formatMessage(
                            {
                              id: "recruiter.metrics.kpi.active_candidates.tooltip",
                              defaultMessage: "{title}: Candidates with at least one non-closed application for your company, excluding candidates already moved to Hired."
                            },
                            {
                              title: intl.formatMessage({ id: "recruiter.metrics.active_candidates", defaultMessage: "Active Candidates" })
                            }
                          )}
                          arrow
                          placement="top"
                        >
                          <Typography variant="body2" sx={{ cursor: "help", width: "fit-content" }}>
                            <FormattedMessage id="recruiter.metrics.active_candidates" defaultMessage="Active Candidates" />
                          </Typography>
                        </MuiTooltip>
                        <Typography variant="h5" sx={{ fontFamily: "Segoe UI Bold, sans-serif" }}>
                          {activeCandidatesValue}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card sx={chartCardSx}>
                <MuiTooltip
                  title={intl.formatMessage(
                    {
                      id: "recruiter.metrics.chart.board_funnel.tooltip",
                      defaultMessage: "{title}: Shows how many candidates entered each kanban stage during the selected period."
                    },
                    {
                      title: intl.formatMessage({ id: "recruiter.metrics.board_funnel", defaultMessage: "Board Funnel" })
                    }
                  )}
                  arrow
                  placement="top"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, width: "fit-content", cursor: "help", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="recruiter.metrics.board_funnel" defaultMessage="Board Funnel" />
                  </Typography>
                </MuiTooltip>
                {!boardFunnel.available ? (
                  emptyChartState(boardFunnel.unavailable_reason || intl.formatMessage({ id: "recruiter.metrics.no_data", defaultMessage: "No data" }))
                ) : (
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, justifyContent: "center", mb: 2 }}>
                    {funnelSeries.map((stage, index) => {
                      const pct = Math.min((Number(stage.entered_count || 0) / funnelMaxValue) * 100, 100);
                      const stageColor = funnelBarPalette[index % funnelBarPalette.length];

                      return (
                        <Box
                          key={`${stage.name}-${index}`}
                          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                          onClick={() => handleFunnelBarClick(stage.id, stage.name)}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: { xs: 74, md: 96 },
                            }}
                          >
                            {stage.name}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 34,
                              borderRadius: 2,
                              overflow: "hidden",
                              bgcolor: theme.palette.secondary.grey_strong
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct}%`,
                                height: "100%",
                                borderRadius: 2,
                                bgcolor: stageColor,
                                transition: "width 700ms ease-out"
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              width: 40,
                              fontFamily: "Segoe UI Bold, sans-serif",                              
                              color: theme.palette.text.main,
                              fontVariantNumeric: "tabular-nums"
                            }}
                          >
                            {stage.entered_count}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={chartCardSx}>
                <MuiTooltip
                  title={intl.formatMessage(
                    {
                      id: "recruiter.metrics.chart.applications_over_time.tooltip",
                      defaultMessage: "{title}: Displays application volume over time."
                    },
                    {
                      title: intl.formatMessage({ id: "recruiter.metrics.applications_over_time", defaultMessage: "Applications" })
                    }
                  )}
                  arrow
                  placement="top"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, width: "fit-content", cursor: "help", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="recruiter.metrics.applications_over_time" defaultMessage="Applications" />
                  </Typography>
                </MuiTooltip>
                {applicationsSeries.length === 0 ? (
                  emptyChartState(applicationsOverTime.unavailable_reason || intl.formatMessage({ id: "recruiter.metrics.no_data", defaultMessage: "No data" }))
                ) : (
                  <Box sx={{ flex: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={applicationsSeries} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                        <CartesianGrid stroke={theme.palette.secondary.grey_strong} vertical={false} />
                        <XAxis dataKey="bucket_label" tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                        <RechartsTooltip
                          formatter={(value) => [
                            Number(value || 0),
                            intl.formatMessage({ id: "menu.applications", defaultMessage: "Applications" })
                          ]}
                        />
                        <Bar
                          dataKey="value"
                          fill={kpiPalette.applications.icon}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={chartCardSx}>
                <MuiTooltip
                  title={intl.formatMessage(
                    {
                      id: "recruiter.metrics.chart.time_to_hire.tooltip",
                      defaultMessage: "{title}: Shows median days from first Pending stage to Hired."
                    },
                    {
                      title: intl.formatMessage({ id: "recruiter.metrics.time_to_hire", defaultMessage: "Time to Hire" })
                    }
                  )}
                  arrow
                  placement="top"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, width: "fit-content", cursor: "help", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="recruiter.metrics.time_to_hire" defaultMessage="Time to Hire" />
                  </Typography>
                </MuiTooltip>
                {!timeToHire.available ? (
                  emptyChartState(timeToHire.unavailable_reason || intl.formatMessage({ id: "recruiter.metrics.no_data", defaultMessage: "No data" }))
                ) : (
                  <Box sx={{ flex: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={timeToHireSeries} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                        <CartesianGrid stroke={theme.palette.secondary.grey_strong} vertical={false} />
                        <XAxis dataKey="bucket_label" tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                        <RechartsTooltip
                          formatter={(value) => [
                            value == null ? "N/A" : Number(value).toFixed(2),
                            intl.formatMessage({ id: "recruiter.metrics.median_days_to_hire", defaultMessage: "Median days to hire" })
                          ]}
                        />
                        <Bar
                          dataKey="median_days"
                          fill={theme.palette.primary.light}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={chartCardSx}>
                <MuiTooltip
                  title={intl.formatMessage(
                    {
                      id: "recruiter.metrics.chart.application_origin.tooltip",
                      defaultMessage: "{title}: Breaks down applications by source — Platform, Mailing, Social, Talent Team, or CVthèque."
                    },
                    {
                      title: intl.formatMessage({ id: "recruiter.metrics.application_origin", defaultMessage: "Job Application Origin" })
                    }
                  )}
                  arrow
                  placement="top"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, width: "fit-content", cursor: "help", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="recruiter.metrics.application_origin" defaultMessage="Job Application Origin" />
                  </Typography>
                </MuiTooltip>
                {applicationOriginSeries.length === 0 ? (
                  emptyChartState(intl.formatMessage({ id: "recruiter.metrics.no_data", defaultMessage: "No data" }))
                ) : (
                  <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={applicationOriginSeries}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={55}
                            outerRadius={90}
                            paddingAngle={2}
                            stroke="none"
                          >
                            {applicationOriginSeries.map((entry, index) => (
                              <Cell key={`${entry.name}-${index}`} fill={applicationOriginColor(entry)} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ minWidth: 170 }}>
                      {applicationOriginSeries.map((entry, index) => (
                        <Box
                          key={`${entry.name}-${index}-legend`}
                          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: applicationOriginColor(entry),
                                flexShrink: 0
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {entry.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: "Segoe UI Bold, sans-serif" }}>
                            {`${Math.round(Number(entry.percent || 0))}%`}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <Modal
        open={funnelModalOpen}
        onClose={() => setFunnelModalOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 3,
            width: { xs: "90vw", sm: 480 },
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
              {funnelModalColumnName}
            </Typography>
            <IconButton size="small" onClick={() => setFunnelModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {funnelModalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : funnelModalCandidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              <FormattedMessage id="recruiter.metrics.no_candidates" defaultMessage="No candidates found" />
            </Typography>
          ) : (
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {funnelModalCandidates.map((candidate) => (
                <Box
                  key={candidate.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: theme.palette.action.hover },
                  }}
                >
                  <Avatar
                    src={candidate.profile_picture_url}
                    sx={{ width: 36, height: 36 }}
                  >
                    {(candidate.full_name || "?")[0]}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }} noWrap>
                      {candidate.full_name}
                    </Typography>
                    {candidate.job_title && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {candidate.job_title}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default DashboardMetrics;
